import React, { useEffect, useRef, useState } from "react";
import { DeckPages } from "./DeckPages";

// Prints only the pages you selected. Two things used to break that: an effect that had already been
// cleaned up could still fire its print (React mounts effects twice in development), so the dialog
// opened after the deck was gone and Chrome printed the whole app; and an early `afterprint` tore the
// deck down before printing. Both are guarded here.
// Dev remounts can leave a second, discarded instance behind; one dialog per package is enough.
let lastPrintAt = 0;

export function PrintDeck({ data, M, title, platform, market, ask, investor, audience, coverPhoto, onDone }) {
  const printed = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer = 0;
    const root = document.documentElement, prev = root.dataset.theme;
    root.dataset.theme = "light";
    document.body.classList.add("printing-deck");
    const restore = () => { root.dataset.theme = prev; document.body.classList.remove("printing-deck"); };
    const after = () => { if (printed.current && !cancelled) { restore(); onDone(); } };
    window.addEventListener("afterprint", after);

    const fire = () => {
      if (cancelled || Date.now() - lastPrintAt < 2000) return;
      lastPrintAt = Date.now();
      setReady(true);
      printed.current = true;
      window.print();
    };
    // Let fonts, charts and images settle first, or the sheets print half-drawn.
    (document.fonts?.ready || Promise.resolve()).then(() => {
      if (cancelled) return;
      timer = setTimeout(() => requestAnimationFrame(fire), 700);
    });

    return () => { cancelled = true; clearTimeout(timer); window.removeEventListener("afterprint", after); restore(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="print-deck" data-testid="print-deck">
      <div className="print-toolbar no-print">
        <span>{ready ? "Choose “Save as PDF”, Letter, landscape." : "Laying out your pages…"}</span>
        <button className="btn btn-sm" onClick={() => window.print()} data-testid="print-deck-again">Print again</button>
        <button className="btn btn-sm" onClick={onDone} data-testid="print-deck-close">Back</button>
      </div>
      <DeckPages data={data} M={M} title={title} platform={platform} market={market} ask={ask} investor={investor} audience={audience} coverPhoto={coverPhoto} />
    </div>
  );
}
